import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, RefreshCw, AlertCircle, Shield } from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { api as axios } from '../lib/api';
import { useI18n } from '../i18n/i18n';

interface LocationCompliancePoint {
  timestamp: string;
  location: string | null;
  vendor: string | null;
  module: string | null;
  sensor: string | null;
  compliance: string | null;
  pue: number | null;
  power_consumption_kw: number | null;
}

interface LocationComplianceResponse {
  data: LocationCompliancePoint[];
  metadata: {
    schema: string;
    table: string;
    limit: number;
    period: string | null;
    from: string | null;
    to: string | null;
    location: string | null;
    count: number;
    generatedAt: string;
  };
}

const PERIOD_OPTIONS = ['4h', '12h', '24h', '3d', '1w', '1M', '2M', '3M', '6M', '12M'] as const;

type PeriodOption = (typeof PERIOD_OPTIONS)[number];

const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const tooltipFormatter: TooltipProps<ValueType, NameType>['formatter'] = (value, name) => {
  if (typeof value === 'number') {
    return [`${numberFormatter.format(value)}`, String(name ?? '')];
  }
  return [String(value), String(name ?? '')];
};

interface LocationComplianceWidgetProps {
  period?: PeriodOption;
  refreshKey?: number;
  hideHeader?: boolean;
  rackId?: string;
  location?: string;
  dateFrom?: string;
  dateTo?: string;
}

const LocationComplianceWidget: React.FC<LocationComplianceWidgetProps> = ({
  period: externalPeriod,
  refreshKey = 0,
  hideHeader = false,
  rackId,
  location,
  dateFrom,
  dateTo
}) => {
  const { t } = useI18n();
  const isMountedRef = useRef(true);
  const [internalPeriod] = useState<PeriodOption>('24h');
  const period = externalPeriod || internalPeriod;
  const [data, setData] = useState<LocationCompliancePoint[]>([]);
  const [metadata, setMetadata] = useState<LocationComplianceResponse['metadata'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const fetchData = useCallback(async (selectedPeriod: PeriodOption, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const params: any = { limit: 500 };

      // Use custom date range if provided, otherwise use period
      if (dateFrom && dateTo) {
        params.from = dateFrom;
        params.to = dateTo;
      } else {
        params.period = selectedPeriod;
      }

      if (rackId) params.rack_id = rackId;
      if (location) params.location = location;

      const response = await axios.get<LocationComplianceResponse>('/api/pue/timeseries', {
        params
      });
      if (!isMountedRef.current) {
        return;
      }
      setData(response.data.data);
      setMetadata(response.data.metadata);
    } catch (err) {
      console.error('Failed to fetch location/compliance data:', err);
      if (!isMountedRef.current) {
        return;
      }
      setError(t('locationCompliance.error.title'));
    } finally {
      if (!options?.silent && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [t, rackId, location, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(period).catch(() => {});
    const interval = window.setInterval(() => {
      fetchData(period, { silent: true }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [fetchData, period, refreshKey]);

  const analytics = useMemo(() => {
    // Location distribution
    const locationCounts = data.reduce((acc, point) => {
      const location = point.location || 'Unknown';
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const locationData = Object.entries(locationCounts).map(([location, count]) => ({
      name: location,
      count,
      percentage: ((count / data.length) * 100).toFixed(1)
    }));

    // Compliance distribution
    const complianceCounts = data.reduce((acc, point) => {
      const compliance = point.compliance || 'Unknown';
      acc[compliance] = (acc[compliance] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const complianceData = Object.entries(complianceCounts).map(([compliance, count]) => ({
      name: compliance,
      count,
      percentage: ((count / data.length) * 100).toFixed(1)
    }));

    // Vendor distribution
    const vendorCounts = data.reduce((acc, point) => {
      const vendor = point.vendor || 'Unknown';
      acc[vendor] = (acc[vendor] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const vendorData = Object.entries(vendorCounts).map(([vendor, count]) => ({
      name: vendor,
      count
    }));

    return {
      locationData: locationData.slice(0, 6), // Top 6 locations
      complianceData: complianceData.slice(0, 6), // Top 6 compliance types
      vendorData: vendorData.slice(0, 5), // Top 5 vendors
      totalLocations: Object.keys(locationCounts).length,
      totalVendors: Object.keys(vendorCounts).length,
      totalDataPoints: data.length
    };
  }, [data]);

  const handleRefresh = () => {
    fetchData(period).catch(() => {});
  };

  const periodLabels = (option: PeriodOption): string => {
    switch (option) {
      case '4h': return t('pue.period.4h');
      case '12h': return t('pue.period.12h');
      case '24h': return t('pue.period.24h');
      case '3d': return t('pue.period.3d');
      case '1w': return t('pue.period.1w');
      case '1M': return t('pue.period.1M');
      case '2M': return t('pue.period.2M');
      case '3M': return t('pue.period.3M');
      case '6M': return t('pue.period.6M');
      case '12M': return t('pue.period.12M');
      default: return option;
    }
  };

  return (
    <div className="card card-hover p-3 flex flex-col h-full overflow-hidden">
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-green-600" />
            <h3 className="text-sm font-semibold text-gray-900">{t('locationCompliance.title')}</h3>
          </div>
          <button onClick={handleRefresh} className="btn-secondary text-xs flex items-center px-2 py-1">
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      )}

      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <AlertCircle className="h-8 w-8 text-error-500 mb-2" />
          <p className="text-sm font-medium text-error-600">{error}</p>
          <button onClick={handleRefresh} className="btn-primary mt-2 text-xs">{t('pue.error.retry')}</button>
        </div>
      ) : loading && !data.length ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <div className="loading-spinner h-6 w-6 mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <AlertCircle className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-gray-500 text-sm">{t('locationCompliance.noData')}</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            <div className="bg-green-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-green-600 tracking-wide">{t('locationCompliance.metrics.locations')}</p>
              <p className="text-sm font-semibold text-gray-900">{numberFormatter.format(analytics.totalLocations)}</p>
            </div>
            <div className="bg-blue-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-blue-600 tracking-wide">{t('locationCompliance.metrics.vendors')}</p>
              <p className="text-sm font-semibold text-gray-900">{numberFormatter.format(analytics.totalVendors)}</p>
            </div>
            <div className="bg-purple-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-purple-600 tracking-wide">{t('locationCompliance.metrics.dataPoints')}</p>
              <p className="text-sm font-semibold text-gray-900">{numberFormatter.format(analytics.totalDataPoints)}</p>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
            {/* Location Distribution */}
            <div className="bg-gray-50 rounded p-2">
              <h4 className="text-[10px] font-medium text-gray-600 mb-1 flex items-center">
                <MapPin className="h-3 w-3 mr-1" />
                {t('locationCompliance.locationDistribution')}
              </h4>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={analytics.locationData}
                    cx="50%"
                    cy="50%"
                    outerRadius={50}
                    dataKey="count"
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    labelLine={false}
                  >
                    {analytics.locationData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={tooltipFormatter} contentStyle={{ fontSize: 9 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Compliance Status */}
            <div className="bg-gray-50 rounded p-2">
              <h4 className="text-[10px] font-medium text-gray-600 mb-1 flex items-center">
                <Shield className="h-3 w-3 mr-1" />
                {t('locationCompliance.complianceStatus')}
              </h4>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={analytics.complianceData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 7, fill: '#6b7280' }} angle={-45} textAnchor="end" height={40} />
                  <YAxis tick={{ fontSize: 8, fill: '#6b7280' }} />
                  <Tooltip formatter={tooltipFormatter} contentStyle={{ fontSize: 9 }} />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="text-[10px] text-gray-500 mt-1 flex justify-between">
            <span>{t('pue.updated')} {metadata?.generatedAt ? new Date(metadata.generatedAt).toLocaleString() : ''}</span>
            <span>{t('locationCompliance.period')}: {periodLabels(period)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default LocationComplianceWidget;